import { useEffect, useMemo, useState } from "react";
import { useGetAllCounties, useGetSettlementsForCounty } from "./accountHooks";
import { OptionType } from "../components";
import { UseQueryResult } from "@tanstack/react-query";
import { CountyResponse, SettlementResponse } from "services";

interface UseCountiesAndSettlementsReturn {
    counties: UseQueryResult<CountyResponse[], Error>;
    countyOptions?: OptionType<number | null>[];
    selectedCounty?: CountyResponse;
    areCountiesLoading: boolean;

    settlements: UseQueryResult<SettlementResponse[], Error>;
    settlementOptions?: OptionType<number | null>[];
    selectedSettlement?: SettlementResponse;
    loadSettlements: boolean;
    areSettlementsLoading: boolean;
}

interface UseCountiesAndSettlementsProps {
    settlement?: OptionType<number | null>;
    county?: OptionType<number | null>;
    loadCounties: boolean;
}

export const useCountiesAndSettlements = ({
    settlement,
    county,
    loadCounties,
}: UseCountiesAndSettlementsProps): UseCountiesAndSettlementsReturn => {
    const [loadSettlements, setLoadSettlements] = useState(Boolean(settlement));

    const counties = useGetAllCounties(loadCounties);
    const settlements = useGetSettlementsForCounty(county?.value ?? undefined, loadSettlements);

    const countyOptions: OptionType<number | null>[] | undefined = counties.data?.map(
        ({ id, name }) => ({
            label: name,
            value: id,
        })
    );
    const settlementOptions: OptionType<number | null>[] | undefined = settlements.data?.map(
        ({ id, name }) => ({
            label: name,
            value: id,
        })
    );

    const selectedSettlement = useMemo(
        () => settlements.data?.find(({ id }) => id === settlement?.value),
        [settlement?.value, settlements.data]
    );

    const selectedCounty = useMemo(
        () => counties.data?.find(({ id }) => id === county?.value),
        [county?.value, counties.data]
    );

    useEffect(() => {
        const countyId = county?.value;
        if (countyId == undefined || countyId == null) return;

        setLoadSettlements(true);
    }, [county?.value]);

    return {
        counties,
        countyOptions,
        selectedCounty,
        areCountiesLoading: counties.isLoading && loadCounties,

        settlements,
        settlementOptions,
        selectedSettlement,
        areSettlementsLoading: settlements.isLoading && loadSettlements,
        loadSettlements,
    };
};
